import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Boxes, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "material", label: "Material" },
  { value: "service", label: "Service" },
  { value: "rent", label: "Rent" },
];
const TYPE_CLS = {
  material: "text-sky-300 border-sky-500/30",
  service: "text-violet-300 border-violet-500/30",
  rent: "text-amber-300 border-amber-500/30",
};
const empty = { name: "", sku: "", item_type: "material", category: "", unit: "pcs", min_stock: "", opening_stock: "" };

export default function Items() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/items").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, sku: it.sku, item_type: it.item_type || "material", category: it.category, unit: it.unit, min_stock: it.min_stock, opening_stock: it.opening_stock });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/items/${editing.id}`, {
          name: form.name, sku: form.sku, item_type: form.item_type, category: form.category, unit: form.unit,
          min_stock: parseFloat(form.min_stock || 0),
        });
        toast.success("Item updated");
      } else {
        await api.post("/items", {
          ...form, min_stock: parseFloat(form.min_stock || 0), opening_stock: parseFloat(form.opening_stock || 0),
        });
        toast.success("Item added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/items/${id}`);
      toast.success("Item deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="items-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Items & Stock Levels</h1>
          <p className="text-muted-foreground mt-1">Master list with live stock balances.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-item-btn" onClick={openNew} className="bg-sky-500 hover:bg-sky-600 text-white gap-2">
              <Plus className="h-4 w-4" /> Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Item" : "Add Item"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input data-testid="item-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cement Bag" />
                </div>
                <div className="space-y-2">
                  <Label>SKU</Label>
                  <Input data-testid="item-sku-input" required value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="CEM-001" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v, unit: v === "service" ? "job" : v === "rent" ? "day" : form.unit })}>
                    <SelectTrigger data-testid="item-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input data-testid="item-category-input" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={form.item_type === "rent" ? "Machinery / Vehicle" : form.item_type === "service" ? "Labour / Transport" : "Material"} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Input data-testid="item-unit-input" value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder={form.item_type === "material" ? "pcs / kg / bag" : "day / hour / job"} />
                </div>
              </div>
              {form.item_type === "material" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Stock Alert</Label>
                  <Input data-testid="item-minstock-input" type="number" step="0.01" min="0" value={form.min_stock}
                    onChange={(e) => setForm({ ...form, min_stock: e.target.value })} placeholder="10" />
                </div>
                {!editing && (
                  <div className="space-y-2">
                    <Label>Opening Stock</Label>
                    <Input data-testid="item-opening-input" type="number" step="0.01" min="0" value={form.opening_stock}
                      onChange={(e) => setForm({ ...form, opening_stock: e.target.value })} placeholder="0" />
                  </div>
                )}
              </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="item-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">
                  {editing ? "Update" : "Save Item"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Current Stock</th>
                <th className="px-4 py-3 font-medium text-right">Min</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="items-table-body">
              {items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                  <Boxes className="h-10 w-10 mx-auto mb-2 opacity-40" /> No items added yet.
                </td></tr>
              ) : items.map((it) => {
                const type = it.item_type || "material";
                const isMat = type === "material";
                const low = isMat && (it.current_stock ?? 0) <= (it.min_stock ?? 0);
                return (
                  <tr key={it.id} className="border-b border-border/50 hover:bg-white/5">
                    <td className="px-4 py-3 font-medium">{it.name}</td>
                    <td className="px-4 py-3 font-mono-sku text-xs text-muted-foreground">{it.sku}</td>
                    <td className="px-4 py-3"><Badge variant="outline" data-testid={`item-type-${it.id}`} className={TYPE_CLS[type]}>{type.charAt(0).toUpperCase() + type.slice(1)}</Badge></td>
                    <td className="px-4 py-3">{it.category || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-sku font-semibold">{isMat ? `${it.current_stock} ${it.unit}` : <span className="text-muted-foreground">N/A</span>}</td>
                    <td className="px-4 py-3 text-right font-mono-sku text-muted-foreground">{isMat ? it.min_stock : "—"}</td>
                    <td className="px-4 py-3">
                      <Badge data-testid="stock-status-badge" variant="outline"
                        className={!isMat ? "text-muted-foreground border-border" : low ? "text-amber-400 border-amber-500/40 bg-amber-500/10" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"}>
                        {!isMat ? "No Stock" : low ? "Low Stock" : "In Stock"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isAdmin && (
                      <Button size="icon" variant="ghost" data-testid={`item-edit-${it.id}`} onClick={() => openEdit(it)}
                        className="h-8 w-8 text-sky-400 hover:bg-sky-500/10"><Pencil className="h-4 w-4" /></Button>
                      )}
                      {isAdmin && (
                        <Button size="icon" variant="ghost" data-testid={`item-delete-${it.id}`} onClick={() => del(it.id)}
                          className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
